import React, { useCallback, useMemo } from 'react';
import copy from 'clipboard-copy';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { Group, Container, FileBlock, Icon, FileContent, Title, Text, MoreIcon, Image, FileIcon } from './styles';
import {
  FILE_TYPES,
  FILES_MENU_OPTIONS,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  copyLinkSupportedTypes,
  ICON_SIZE
} from '../../global/constants';
import { Dropdown, DropdownItem } from '../dropdown';
import { RawProjectFile } from '../../mobx/interfaces/project';
import IconButton from '../button/iconButton';
import { rootStore } from '../../mobx/rootStore';
import Tooltip from '../tooltip';
import { useMobileDevice } from '../../global/useMobile';
interface FileDataPropsInterfacse {
  header: React.ReactNode;
  title: string;
  endtext: string;
  endTextTooltip?: string;
  itemData: RawProjectFile;
  fileType?: number;
  onClickFile: () => void;
  onClickRename?: () => void;
  OpenModal?: () => void;
  Opendeletemodel?: () => void;
  isSelected: boolean;
  setCurrentItem: (item: RawProjectFile | null) => void;
  isImage?: boolean;
}

const Filedata: React.FC<FileDataPropsInterfacse> = (props) => {
  const { onClickFile, onClickRename, OpenModal, Opendeletemodel, isImage, isSelected, itemData, setCurrentItem } =
    props;
  const isMobile = useMobileDevice();

  // File types that support copy link functionality

  const canShowCopyLink = copyLinkSupportedTypes.includes(itemData.uploadFileType);

  // Generate menu items based on file type
  const menuItems = useMemo(() => {
    const items = [{ key: FILES_MENU_OPTIONS.VIEW, label: 'View' }];

    if (canShowCopyLink) {
      items.push({ key: FILES_MENU_OPTIONS.COPYLINK, label: 'Copy link' });
    }

    if (itemData.uploadFileType === FILE_TYPES.UPLOAD) {
      items.push({ key: FILES_MENU_OPTIONS.DOWNLOAD, label: 'Download' });
    }

    items.push(
      { key: FILES_MENU_OPTIONS.RENAME, label: 'Rename' },
      { key: FILES_MENU_OPTIONS.MOVETOFOLDER, label: 'Move to folder' },
      { key: FILES_MENU_OPTIONS.DELETE, label: 'Delete' }
    );

    return items;
  }, [itemData.uploadFileType, canShowCopyLink]);

  // Handle menu item click
  const handleMenuClick = useCallback(
    async (key: number | string) => {
      key = Number(key);
      if (key === FILES_MENU_OPTIONS.VIEW) {
        onClickFile();
        setCurrentItem(null);
      } else if (key === FILES_MENU_OPTIONS.COPYLINK) {
        if (itemData.url) {
          try {
            await copy(itemData.url);
            rootStore.alertNotificationStore.setSuccessMessage('Link copied successfully!');
            setCurrentItem(null);
          } catch (error) {
            rootStore.alertNotificationStore.setErrorMessage('Failed to copy link');
          }
        }
      } else if (key === FILES_MENU_OPTIONS.RENAME) {
        if (onClickRename) onClickRename();
      } else if (key === FILES_MENU_OPTIONS.MOVETOFOLDER) {
        if (OpenModal) OpenModal();
      } else if (key === FILES_MENU_OPTIONS.DELETE) {
        if (Opendeletemodel) Opendeletemodel();
      } else if (key === FILES_MENU_OPTIONS.DOWNLOAD) {
        if (itemData?.url && itemData?.name) {
          fetch(itemData.url)
            .then((response) => {
              if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.statusText}`);
              }
              return response.blob();
            })
            .then((blob) => {
              const blobUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = itemData.name;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
            })
            .catch((error) => {
              console.error('Error during download:', error);
            });
          setCurrentItem(null);
        } else {
          console.warn('Invalid file URL or name:', itemData);
        }
      }
    },
    [onClickFile, itemData, setCurrentItem, onClickRename, OpenModal, Opendeletemodel]
  );
  // Memoized dropdown items
  const MenuItemDropdown = useMemo(() => {
    return menuItems.map((item) => (
      <DropdownItem
        label={item.label}
        key={item.key}
        onClick={() => handleMenuClick(item.key)}
        tone={item.label === 'Delete' ? 'critical' : 'primary'}
      />
    ));
  }, [menuItems, handleMenuClick]);

  return (
    <Group role='button' onClick={(e) => e.stopPropagation()}>
      <Container role='button' onClick={onClickFile}>
        <FileBlock isActive={isSelected}>
          <Image isImage={isImage}>
            {!isImage && (
              <FileIcon>
                {props.fileType === FILE_TYPES.DRIVE && (
                  <SVGIcon
                    name='file-googledrive-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    className='fillColor'
                  />
                )}
                {props.fileType === FILE_TYPES.ONEDRIVE && (
                  <SVGIcon
                    name='file-onedrive-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    className='fillColor'
                  />
                )}
                {props.fileType === FILE_TYPES.DROPBOX && (
                  <SVGIcon
                    name='file-dropbox-icon'
                    width={ICON_SIZE.LG}
                    height={ICON_SIZE.LG}
                    viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                    className='fillColor'
                  />
                )}
              </FileIcon>
            )}
            <Icon isImage={isImage}>{props.header}</Icon>
            <MoreIcon className='active-dropdown' isImage={isImage}>
              <Dropdown
                modalTitle='Manage File'
                content={
                  <IconButton
                    iconName='three-dots-horizontal-icon'
                    size={ICON_BUTTON_SIZE.SMALL}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={() => setCurrentItem(itemData)}
                  />
                }
                trigger='click'
                onOutsideClick={() => setCurrentItem(null)}>
                {MenuItemDropdown}
              </Dropdown>
            </MoreIcon>
          </Image>
          <FileContent>
            <Title>{props.title}</Title>
            {props.endTextTooltip && !isMobile ? (
              <Tooltip title={props.endTextTooltip} maxWidth='max-content'>
                <Text>{props.endtext}</Text>
              </Tooltip>
            ) : (
              <Text>{props.endtext}</Text>
            )}
          </FileContent>
        </FileBlock>
      </Container>
    </Group>
  );
};

export default React.memo(Filedata);
